import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../services/apiService';
import { authService } from '../services/authService';
import { showToast } from '../components/Toast';
import ConfirmModal from '../components/ConfirmModal';
import BottomSheet from '../components/BottomSheet';
import ActionsMenu from '../components/ActionsMenu';
import { DSBackLink, DSButton, DSInput, DSSelect, DSCard, DSBadge, DSTypeaheadFilterSelect, DSIcon } from '../design-system/primitives';
import PinGolfBottomNav from '../components/PinGolfBottomNav';
import PinGolfHoleList from '../components/PinGolfHoleList';
import PinGolfSeasonSettings from '../components/PinGolfSeasonSettings';
import PinGolfWeekSettlement from '../components/PinGolfWeekSettlement';
import pingolfHero from '../assets/pingolf-hero.webp';
import { autoPickBlocker, countMachineUsage, describeTargetRefresh, isAutoPickable, pickBalancedMachines, HOLES_PER_WEEK } from '../utils/pingolfLineup';
import './PinGolfLeagueAdmin.css';
import { personName } from '../utils/personName';

const emptyHole = () => ({ machineId: '', targetScore: '', maxBalls: 3 });

const SEASON_STATUS_VARIANT = {
  active: 'success',
  completed: 'warning'
};

const hostDisplayName = (user) => personName(user)
  || user.username
  || user.email;

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const formatTimeOfDay = (timeOfDay) => {
  if (!timeOfDay) return '';
  const [hourStr, minuteStr] = timeOfDay.split(':');
  const hour = parseInt(hourStr, 10);
  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = ((hour + 11) % 12) + 1;
  return `${displayHour}:${minuteStr} ${period}`;
};

// Group skip reasons (e.g. "week has no lineup yet") so a whole-season skip reads as
// one clear sentence instead of a wall of per-week entries.
const summarizeSkipped = (skipped) => {
  const reasonCounts = new Map();
  for (const item of skipped) {
    reasonCounts.set(item.reason, (reasonCounts.get(item.reason) || 0) + 1);
  }
  return Array.from(reasonCounts.entries()).map(([reason, count]) => `${count} ${reason}`).join('; ');
};

const formatSlotDuration = (minutes) => {
  const total = parseInt(minutes, 10);
  if (!Number.isInteger(total) || total <= 0) return 'No length set';
  const hours = Math.floor(total / 60);
  const remainder = total % 60;
  if (!hours) return `${remainder} min session`;
  return remainder ? `${hours} hr ${remainder} min session` : `${hours} hr session`;
};

// season.start_date arrives as a date-only or UTC-midnight string; parse the parts by
// hand and rebuild in local time so it doesn't render as the previous day west of UTC.
const formatStartDate = (rawDate) => {
  if (!rawDate) return '';
  const [year, month, day] = rawDate.slice(0, 10).split('-').map(Number);
  if (!year || !month || !day) return '';
  return new Date(year, month - 1, day).toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'
  });
};

function HostSlotsSection({ season, hostSlots, hosts, weeksGenerated, weeksMissingSessions, onGenerateSchedule, onGenerateHostNights, generating, onChanged }) {
  const savedStartDate = season.start_date ? season.start_date.slice(0, 10) : '';
  const isLocked = season.status === 'completed';
  const [startDate, setStartDate] = useState(savedStartDate);
  // Start in edit mode only when there's nothing saved yet; once saved this collapses
  // to a read-only line with an Edit button, so the save actually shows a result.
  const [editingStartDate, setEditingStartDate] = useState(!savedStartDate);
  const [slotToDelete, setSlotToDelete] = useState(null);
  const [slotDay, setSlotDay] = useState('2');
  const [slotTime, setSlotTime] = useState('18:00');
  const [slotDuration, setSlotDuration] = useState('240');
  const [slotHostId, setSlotHostId] = useState('');
  const [showSlotModal, setShowSlotModal] = useState(false);
  const [busy, setBusy] = useState(false);

  const saveStartDate = async () => {
    if (!startDate) {
      showToast('Pick a start date first', 'warning');
      return;
    }
    setBusy(true);
    try {
      await apiService.setPingolfSeasonStartDate(season.id, startDate);
      showToast('Season start date saved', 'success');
      setEditingStartDate(false);
      onChanged();
    } catch (error) {
      showToast(error.response?.data?.error || 'Failed to save start date', 'error');
    } finally {
      setBusy(false);
    }
  };

  const addSlot = async () => {
    if (!slotHostId) {
      showToast('Pick a host for this slot', 'warning');
      return;
    }
    setBusy(true);
    try {
      await apiService.addPingolfHostSlot(season.id, {
        dayOfWeek: parseInt(slotDay, 10),
        timeOfDay: slotTime,
        durationMinutes: parseInt(slotDuration, 10) || 240,
        hostUserId: slotHostId
      });
      showToast('Recurring host slot added', 'success');
      setSlotHostId('');
      setShowSlotModal(false);
      onChanged();
    } catch (error) {
      showToast(error.response?.data?.error || 'Failed to add host slot', 'error');
    } finally {
      setBusy(false);
    }
  };

  const deleteSlot = async () => {
    const slot = slotToDelete;
    if (!slot) return;
    setBusy(true);
    try {
      await apiService.removePingolfHostSlot(season.id, slot.id);
      showToast('Recurring host slot removed', 'success');
      setSlotToDelete(null);
      onChanged();
    } catch (error) {
      showToast(error.response?.data?.error || 'Failed to remove host slot', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="pl-admin-section">
      <div className="pl-admin-week-section-title">Recurring host nights</div>

      <div className="pl-admin-week-section">
        {editingStartDate ? (
          <>
            <div className="pl-admin-hole-actions">
              <DSInput type="date" value={startDate} onChange={e => setStartDate(e.target.value)} disabled={isLocked} />
              <DSButton size="sm" onClick={saveStartDate} disabled={busy || isLocked}>
                Save start date
              </DSButton>
              {savedStartDate && (
                <DSButton
                  appearance="text"
                  tone="neutral"
                  size="sm"
                  onClick={() => { setStartDate(savedStartDate); setEditingStartDate(false); }}
                  disabled={busy}
                >
                  Cancel
                </DSButton>
              )}
            </div>
            <p className="pl-admin-field-help">
              Week 1 starts on this date. Every later week is scheduled 7 days further out, so set it before adding host slots.
            </p>
          </>
        ) : (
          <div className="pl-admin-readonly-row">
            <div className="pl-admin-readonly-field">
              <span className="pl-admin-readonly-label">Week 1 starts</span>
              <span className="pl-admin-readonly-value">{formatStartDate(savedStartDate)}</span>
            </div>
            {!isLocked && (
              <DSButton appearance="outline" tone="neutral" size="sm" onClick={() => setEditingStartDate(true)} disabled={busy}>
                Edit
              </DSButton>
            )}
          </div>
        )}
      </div>

      <div className="pl-admin-week-section">
        {!savedStartDate ? (
          <p className="pl-admin-empty">Set the Week 1 start date above to start adding recurring host slots.</p>
        ) : (
          <>
            <ul className="pl-admin-session-list">
              {hostSlots.map(slot => (
                <li key={slot.id} className="pl-admin-session-item">
                  <div className="pl-admin-session-item-info">
                    <span className="pl-admin-session-item-title">
                      {DAY_NAMES[slot.day_of_week]} {formatTimeOfDay(slot.time_of_day)} - {hostDisplayName(slot)}
                    </span>
                    <span className="pl-admin-session-item-date">
                      {formatSlotDuration(slot.duration_minutes)}
                      {slot.session_label ? ` - ${slot.session_label}` : ''}
                    </span>
                  </div>
                  {!isLocked && (
                    <DSButton appearance="text" tone="danger" size="sm" onClick={() => setSlotToDelete(slot)} disabled={busy} title="Remove this recurring slot">
                      Delete
                    </DSButton>
                  )}
                </li>
              ))}
              {hostSlots.length === 0 && <li className="pl-admin-empty">No recurring host slots yet.</li>}
            </ul>

            {!isLocked && (
              <div className="pl-admin-session-add-row">
                <DSButton appearance="outline" tone="neutral" size="sm" onClick={() => setShowSlotModal(true)}>+ Add recurring slot</DSButton>
              </div>
            )}

            {/* Two states. Before weeks exist: the one-button build-out. After: a re-run,
                shown only while some week still has no host night - which is how a week whose
                slot was added later gets picked up. */}
            {!isLocked && hostSlots.length > 0 && !weeksGenerated && (
              <div className="pl-admin-generate-row">
                <DSButton onClick={onGenerateSchedule} disabled={busy || generating}>
                  Generate {season.week_count} weeks and host nights
                </DSButton>
                <p className="pl-admin-field-help">
                  Creates every week with a randomized machine lineup, then puts a host night on
                  the calendar for each week and slot above. Holes with no target score are
                  flagged below rather than held back - the week's course snapshots into its
                  nights once every target is set, which has to happen before cards go on sale.
                </p>
              </div>
            )}

            {!isLocked && hostSlots.length > 0 && weeksGenerated && weeksMissingSessions > 0 && (
              <div className="pl-admin-generate-row">
                <DSButton onClick={onGenerateHostNights} disabled={busy || generating}>
                  Generate host nights
                </DSButton>
                <p className="pl-admin-field-help">
                  {weeksMissingSessions} week{weeksMissingSessions === 1 ? '' : 's'} still {weeksMissingSessions === 1 ? 'has' : 'have'} no
                  host night. Missing target scores no longer hold this up - the nights go on the
                  calendar now, and each week's course fills in as soon as its last target is set.
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {showSlotModal && (
        <BottomSheet
          title="Add recurring slot"
          onClose={() => setShowSlotModal(false)}
          footer={(
            <>
              <DSButton variant="secondary" onClick={() => setShowSlotModal(false)} disabled={busy}>Cancel</DSButton>
              <DSButton onClick={addSlot} disabled={busy}>Add recurring slot</DSButton>
            </>
          )}
        >
          <DSSelect id="pl-slot-day" label="Night of the week" value={slotDay} onChange={e => setSlotDay(e.target.value)}>
            {DAY_NAMES.map((name, index) => (
              <option key={name} value={index}>{name}</option>
            ))}
          </DSSelect>
          <DSInput id="pl-slot-time" label="Start time" type="time" value={slotTime} onChange={e => setSlotTime(e.target.value)} />
          <DSInput
            id="pl-slot-duration"
            label="Session length (minutes)"
            helpText="How long the night runs. 240 is a 4-hour night."
            type="number"
            min="30"
            step="30"
            value={slotDuration}
            onChange={e => setSlotDuration(e.target.value)}
          />
          <DSTypeaheadFilterSelect
            id="pl-slot-host"
            label="Host"
            placeholder="Search for a host..."
            emptyMessage="No hosts found"
            options={hosts.map(host => ({ value: host.id, label: hostDisplayName(host) }))}
            selectedValues={slotHostId ? [slotHostId] : []}
            onChange={(values) => setSlotHostId(values.length > 0 ? values[values.length - 1] : '')}
          />
        </BottomSheet>
      )}

      {slotToDelete && (
        <ConfirmModal
          title="Remove recurring slot?"
          message={`Remove the recurring ${DAY_NAMES[slotToDelete.day_of_week]} slot for ${hostDisplayName(slotToDelete)}? This does not affect nights already generated from it.`}
          confirmLabel="Remove slot"
          danger
          submitting={busy}
          onConfirm={deleteSlot}
          onCancel={() => setSlotToDelete(null)}
        />
      )}
    </section>
  );
}

// A randomized lineup can pick a machine with no configured target score, which leaves
// the hole unscoreable until someone fills it in. '' / null / undefined all mean unset;
// 0 is not a legal target, so it counts as unset too.
const hasTargetScore = (hole) => {
  const target = Number(hole?.targetScore);
  return Number.isFinite(target) && target > 0;
};

const formatSessionDate = (eventDate) => {
  if (!eventDate) return '';
  const date = new Date(eventDate);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('en-US', {
    month: 'numeric', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit'
  });
};

function WeekCard({ week, weeks, machines, hosts, onChanged }) {
  // Starts empty rather than on a blank hole, so a week with no lineup shows its
  // empty state (and the randomize offer) instead of one unfilled row.
  const [lineup, setLineup] = useState(
    (week.lineup && week.lineup.length > 0) ? week.lineup.map(h => ({ ...h })) : []
  );
  const hasSavedLineup = Boolean(week.lineup && week.lineup.length > 0);
  // The saved lineup can change from outside this card: a machine target edit or "Refresh
  // targets from machines" rewrites it on the server. Without this the card kept showing (and
  // the next save sent back) the targets it was first rendered with.
  const savedLineupKey = JSON.stringify(week.lineup || []);
  useEffect(() => {
    const saved = JSON.parse(savedLineupKey);
    setLineup(saved.length > 0 ? saved.map(h => ({ ...h })) : []);
  }, [savedLineupKey]);
  // Collapsed by default - a generated season is 8+ of these, and the header summary
  // carries enough to tell which weeks still need attention without opening them.
  const [weekExpanded, setWeekExpanded] = useState(false);
  const [sessionTitle, setSessionTitle] = useState('');
  const [sessionDate, setSessionDate] = useState('');
  const [sessionHostId, setSessionHostId] = useState('');
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [sessionToRemove, setSessionToRemove] = useState(null);
  const [showFinalizeConfirm, setShowFinalizeConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  const autoPickMachines = machines.filter(isAutoPickable);

  const updateHole = (index, field, value) => {
    setLineup(prev => prev.map((hole, i) => (i === index ? { ...hole, [field]: value } : hole)));
  };

  const selectMachine = (index, machineIdValue) => {
    const machine = machines.find(m => String(m.id) === machineIdValue);
    setLineup(prev => prev.map((hole, i) => {
      if (i !== index) return hole;
      return {
        ...hole,
        machineId: machineIdValue,
        targetScore: machine?.pingolf_target_score ?? hole.targetScore,
        maxBalls: machine?.pingolf_ball_count || hole.maxBalls || 3
      };
    }));
  };

  // Fills out the week's lineup in one click instead of hand-picking each hole. Target
  // score/max balls default in from the machine record when set (same as picking one
  // manually); machines without a target still get picked, just with that field left blank
  // to fill in by hand - not having every machine pre-configured shouldn't block getting a
  // lineup together.
  //
  // The picks are weighted against the rest of the season, so re-randomizing one week
  // reaches for the machines the other weeks haven't used rather than re-drawing blind
  // and re-creating the lopsided spread this is meant to avoid. Only operational machines
  // are drawn; anything else on the floor stays hand-pick-only.
  const randomizeLineup = () => {
    if (autoPickMachines.length === 0) {
      showToast('No operational machines available to randomize from', 'warning');
      return;
    }
    if (autoPickMachines.length < HOLES_PER_WEEK) {
      showToast(`Only ${autoPickMachines.length} operational machine${autoPickMachines.length === 1 ? '' : 's'} available - filling the lineup with all of them`, 'warning');
    }

    // This week's own (empty) lineup is excluded so a re-randomize doesn't count itself.
    const usage = countMachineUsage((weeks || []).filter(w => w.id !== week.id));
    const picked = pickBalancedMachines(autoPickMachines, usage, HOLES_PER_WEEK);

    const randomized = picked.map(machine => ({
      machineId: String(machine.id),
      targetScore: machine.pingolf_target_score ?? '',
      maxBalls: machine.pingolf_ball_count || 3
    }));
    setLineup(randomized);
    persistLineup(randomized);
  };

  // The lineup saves per hole rather than behind a Save button: a hole is done when its
  // sheet closes, and there is no partial state worth holding on the client. `next` is
  // passed in rather than read off state because the caller has just computed it - a
  // setLineup in the same tick has not landed yet.
  //
  // Holes with no machine picked are dropped from the write rather than blocking it. An
  // added-but-unfilled hole is a normal in-between state now that every edit saves, and
  // it comes back from the server as simply not there yet.
  const persistLineup = async (next) => {
    const normalized = next
      .filter(h => h.machineId !== '' && h.machineId !== null && h.machineId !== undefined)
      .map(h => ({
        machineId: parseInt(h.machineId, 10),
        targetScore: h.targetScore === '' || h.targetScore === null || h.targetScore === undefined ? undefined : parseInt(h.targetScore, 10),
        maxBalls: parseInt(h.maxBalls, 10) || 3
      }));

    // A lineup can't repeat a machine across holes - attaching a session later would
    // crash on the DB's UNIQUE(event_id, machine_id) constraint. Catch it here, at save
    // time, with the machine's name, instead of surfacing it downstream by ID when a
    // host night is created. The duplicate stays on screen unsaved, so the fix is to
    // reopen that hole and pick something else.
    const seenMachineIds = new Set();
    for (const hole of normalized) {
      if (seenMachineIds.has(hole.machineId)) {
        const machine = machines.find(m => m.id === hole.machineId);
        showToast(`"${machine?.name || hole.machineId}" is used more than once in this lineup. Each machine can only appear once.`, 'warning');
        return;
      }
      seenMachineIds.add(hole.machineId);
    }

    setBusy(true);
    try {
      await apiService.setPingolfWeekLineup(week.id, normalized);
      showToast('Lineup saved', 'success');
      onChanged();
    } catch (error) {
      showToast(error.response?.data?.error || 'Failed to save lineup', 'error');
    } finally {
      setBusy(false);
    }
  };

  const createSession = async () => {
    if (!sessionTitle || !sessionDate || !sessionHostId) {
      showToast('Title, date, and host are required', 'warning');
      return;
    }
    setBusy(true);
    try {
      await apiService.createPingolfWeekSession(week.id, {
        title: sessionTitle,
        eventDate: sessionDate,
        hostUserId: sessionHostId
      });
      showToast('Host night created and attached', 'success');
      setSessionTitle('');
      setSessionDate('');
      setSessionHostId('');
      setShowSessionModal(false);
      onChanged();
    } catch (error) {
      showToast(error.response?.data?.error || 'Failed to create session', 'error');
    } finally {
      setBusy(false);
    }
  };

  // A night's course is a snapshot of the week lineup taken when the night was attached, so
  // the two can end up out of step - a lineup edited afterwards, or a course that failed to
  // cut. The player's card reads the course while this page reads the lineup, so the gap is
  // invisible from here unless we say so. Locked once cards exist: at that point the course
  // is what someone is playing.
  //
  // The verdict comes from the server (`course_matches_lineup`), which compares hole for hole.
  // Comparing hole counts here was not enough: a host who finds a machine down on arrival swaps
  // it one for one, the count never moves, and the mismatch went unreported.
  //
  // A missing field is NOT the same as a mismatch, and collapsing the two is what made a server
  // that predates this field report "no course cut" on nights that were perfectly fine. Absent
  // means we do not know, and a warning we cannot substantiate is worse than no warning at all.
  const courseMismatch = (session) => {
    if (!hasSavedLineup) return false;
    if (session.course_matches_lineup === null || session.course_matches_lineup === undefined) return false;
    return session.course_matches_lineup === false;
  };

  const resyncCourse = async (session) => {
    setBusy(true);
    try {
      await apiService.resyncPingolfSessionCourse(week.id, session.id);
      showToast('Course rebuilt from the week lineup', 'success');
      onChanged();
    } catch (error) {
      showToast(error.response?.data?.error || 'Failed to rebuild the course', 'error');
    } finally {
      setBusy(false);
    }
  };

  const removeSession = async () => {
    setBusy(true);
    try {
      await apiService.removePingolfWeekSession(week.id, sessionToRemove.id);
      showToast('Host night removed', 'success');
      setSessionToRemove(null);
      onChanged();
    } catch (error) {
      showToast(error.response?.data?.error || 'Failed to remove session', 'error');
    } finally {
      setBusy(false);
    }
  };

  // Header summary, read off the saved lineup (not the in-progress edit buffer) so a
  // collapsed card always reflects what's actually stored.
  const savedHoles = week.lineup || [];
  const sessionCount = (week.sessions || []).length;
  const holeCount = savedHoles.length;
  const holesMissingTarget = savedHoles.filter(hole => !hasTargetScore(hole)).length;
  // Nights whose course doesn't match the lineup. Surfaced on the collapsed header because a
  // season is 8+ of these cards and nobody expands a week that looks fine - which is exactly
  // how a broken course reaches league night undiscovered.
  const sessionsWithBadCourse = (week.sessions || []).filter(
    session => hasSavedLineup && session.course_matches_lineup === false
  ).length;

  const finalizeWeek = async () => {
    setBusy(true);
    try {
      const result = await apiService.finalizePingolfWeek(week.id);
      showToast(`Week ${week.week_number} finalized - $${result.data.total_awards} paid out`, 'success');
      setShowFinalizeConfirm(false);
      onChanged();
    } catch (error) {
      showToast(error.response?.data?.error || 'Failed to finalize week', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <DSCard className="pl-admin-week-card">
      <div
        className="pl-admin-week-header pl-admin-week-header-collapsible"
        role="button"
        tabIndex={0}
        onClick={() => setWeekExpanded(prev => !prev)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setWeekExpanded(prev => !prev);
          }
        }}
      >
        <span className="pl-admin-week-header-title">
          <span className={`pl-admin-week-chevron ${weekExpanded ? 'is-open' : ''}`}>
            <DSIcon name="chevronRight" size="sm" tone="muted" />
          </span>
          <strong>Week {week.week_number}</strong>
        </span>
        <span className="pl-admin-week-header-right">
          {!weekExpanded && (
            <span className="pl-admin-week-header-summary">
              <span className="pl-admin-week-header-stat" title={`${sessionCount} host night${sessionCount === 1 ? '' : 's'}`}>
                <DSIcon name="calendar" size="sm" tone="muted" label="Host nights" />
                {sessionCount}
              </span>
              <span className="pl-admin-week-header-stat" title={`${holeCount} hole${holeCount === 1 ? '' : 's'}`}>
                <DSIcon name="golf" size="sm" tone="success" label="Holes" />
                {holeCount}
              </span>
              {holesMissingTarget > 0 && (
                <span
                  className="pl-admin-week-header-stat pl-admin-week-header-warning"
                  title={`${holesMissingTarget} hole${holesMissingTarget === 1 ? '' : 's'} missing a target score`}
                >
                  <DSIcon name="target" size="sm" tone="danger" label="Holes missing a target score" />
                  {holesMissingTarget}
                </span>
              )}
              {sessionsWithBadCourse > 0 && (
                <span
                  className="pl-admin-week-header-stat pl-admin-week-header-warning"
                  title={`${sessionsWithBadCourse} host night${sessionsWithBadCourse === 1 ? "'s course does not" : "s' courses do not"} match this week's lineup`}
                >
                  <DSIcon name="warning" size="sm" tone="danger" label="Host night course does not match the lineup" />
                  {sessionsWithBadCourse}
                </span>
              )}
            </span>
          )}
          <span className={`pl-admin-week-status pl-admin-week-status-${week.status}`}>{week.status}</span>
        </span>
      </div>

      {weekExpanded && (
        <>
          <div className="pl-admin-week-section">
            {/* Editable in place until the week is finalized - there is no edit toggle
                because each hole saves as its sheet closes, so there is no draft to enter
                or abandon. */}
            {week.status !== 'finalized' ? (
              <>
                <PinGolfHoleList
                  editing
                  machines={machines}
                  disabled={busy}
                  holes={lineup.map((hole, index) => ({
                    key: index,
                    holeNumber: index + 1,
                    machineId: hole.machineId,
                    targetScore: hole.targetScore,
                    maxBalls: hole.maxBalls,
                    targetOverridden: Boolean(hole.targetOverridden)
                  }))}
                  onChangeHole={(key, field, value) => {
                    if (field === 'machineId') {
                      selectMachine(key, value);
                    } else {
                      updateHole(key, field, value);
                    }
                  }}
                  onCommit={() => persistLineup(lineup)}
                  onRemoveHole={(key) => {
                    const next = lineup.filter((_, i) => i !== key);
                    setLineup(next);
                    persistLineup(next);
                  }}
                />

                {/* Randomizing is a way to start a week, not to edit one: once holes exist
                    it would silently throw the lineup away, so it belongs to the empty
                    state only. */}
                {lineup.length === 0 ? (
                  <div className="pl-admin-week-lineup-empty">
                    <p className="pl-admin-empty">No machines picked for this week yet.</p>
                    <div className="pl-admin-hole-actions">
                      <DSButton
                        onClick={randomizeLineup}
                        disabled={busy}
                        title="Fill the lineup with 9 operational machines the rest of the season has used least (using each machine's saved target score)"
                        leadingIcon={<DSIcon name="gamepad" />}
                      >
                        Randomize 9 holes
                      </DSButton>
                      <DSButton variant="secondary" onClick={() => setLineup([emptyHole()])} disabled={busy}>
                        Add hole
                      </DSButton>
                    </div>
                  </div>
                ) : (
                  <div className="pl-admin-hole-actions pl-admin-hole-actions-end">
                    <DSButton
                      appearance="text"
                      tone="neutral"
                      size="sm"
                      onClick={() => setLineup(prev => [...prev, emptyHole()])}
                      disabled={busy}
                    >
                      + Add hole
                    </DSButton>
                  </div>
                )}
              </>
            ) : (
              <PinGolfHoleList
                holes={(week.lineup || []).map((hole, index) => {
                  const machine = machines.find(m => String(m.id) === String(hole.machineId));
                  return {
                    key: index,
                    holeNumber: index + 1,
                    name: machine?.name || `Machine ${hole.machineId}`,
                    targetScore: hole.targetScore,
                    maxBalls: hole.maxBalls || 3,
                    targetOverridden: Boolean(hole.targetOverridden)
                  };
                })}
              />
            )}
          </div>

          <div className="pl-admin-week-section">
            <div className="pl-admin-week-section-title">Host nights ({(week.sessions || []).length})</div>
            <ul className="pl-admin-session-list">
              {(week.sessions || []).map(session => (
                <li key={session.id} className="pl-admin-session-item">
                  <div className="pl-admin-session-item-info">
                    <span className="pl-admin-session-item-title">{session.event_title || session.session_label || 'Host night'}</span>
                    <span className="pl-admin-session-item-date">{formatSessionDate(session.event_date)}</span>
                    {courseMismatch(session) && (
                      <span className="pl-admin-session-item-warning">
                        <DSIcon name="target" size="sm" tone="danger" />
                        {Number(session.hole_count ?? 0) === 0
                          ? `No course cut for this night - players see nothing to play. The week lineup has ${holeCount} ${holeCount === 1 ? 'hole' : 'holes'}.`
                          : Number(session.hole_count) !== holeCount
                            ? `This night's course has ${session.hole_count} of the lineup's ${holeCount} holes - that's all a player can see.`
                            : `This night's course has ${session.hole_count} ${session.hole_count === 1 ? 'hole' : 'holes'} like the lineup, but they are not the same holes - the machines, targets or ball counts differ from what's shown above.`}
                        {Number(session.card_count ?? 0) > 0 && ' Cards are already bought, so it can only be corrected hole by hole from host controls.'}
                      </span>
                    )}
                  </div>
                  <div className="pl-admin-session-item-actions">
                    <a className="pl-admin-link" href={`/events/${session.event_id}`} target="_blank" rel="noreferrer">View</a>
                    {courseMismatch(session) && Number(session.card_count ?? 0) === 0 && week.status !== 'finalized' && (
                      <DSButton
                        appearance="text"
                        size="sm"
                        onClick={() => resyncCourse(session)}
                        disabled={busy}
                        title="Re-cut this night's course from the week lineup"
                      >
                        Rebuild course
                      </DSButton>
                    )}
                    {week.status !== 'finalized' && (
                      <DSButton
                        appearance="text"
                        tone="danger"
                        size="sm"
                        onClick={() => setSessionToRemove(session)}
                        disabled={busy}
                        title="Remove this host night"
                      >
                        Delete
                      </DSButton>
                    )}
                  </div>
                </li>
              ))}
              {(week.sessions || []).length === 0 && <li className="pl-admin-empty">No host nights added yet.</li>}
            </ul>
            {week.status !== 'finalized' && (
              <div className="pl-admin-session-add-row">
                <DSButton appearance="outline" tone="neutral" size="sm" onClick={() => setShowSessionModal(true)}>+ Add host night</DSButton>
              </div>
            )}
          </div>

          {week.status === 'finalized' && (
            <div className="pl-admin-week-section">
              <div className="pl-admin-week-section-title">Settlement</div>
              <PinGolfWeekSettlement weekId={week.id} />
            </div>
          )}

          {week.status === 'open' && (week.sessions || []).length > 0 && (
            <DSButton variant="primary" onClick={() => setShowFinalizeConfirm(true)} disabled={busy}>Finalize Week {week.week_number}</DSButton>
          )}
        </>
      )}

      {showSessionModal && (
        <BottomSheet
          title="Add host night"
          onClose={() => setShowSessionModal(false)}
          footer={(
            <>
              <DSButton variant="secondary" onClick={() => setShowSessionModal(false)} disabled={busy}>Cancel</DSButton>
              <DSButton onClick={createSession} disabled={busy}>Add host night</DSButton>
            </>
          )}
        >
          <DSInput
            id="pl-session-title"
            label="Night title"
            placeholder="e.g. Week 1 - Tuesday"
            value={sessionTitle}
            onChange={e => setSessionTitle(e.target.value)}
          />
          <DSInput
            id="pl-session-date"
            label="Date and start time"
            type="datetime-local"
            value={sessionDate}
            onChange={e => setSessionDate(e.target.value)}
          />
          <DSTypeaheadFilterSelect
            id="pl-session-host"
            label="Host"
            placeholder="Search for a host..."
            emptyMessage="No hosts found"
            options={hosts.map(host => ({ value: host.id, label: hostDisplayName(host) }))}
            selectedValues={sessionHostId ? [sessionHostId] : []}
            onChange={(values) => setSessionHostId(values.length > 0 ? values[values.length - 1] : '')}
          />
        </BottomSheet>
      )}

      {sessionToRemove && (
        <ConfirmModal
          title="Remove host night?"
          message={`Remove "${sessionToRemove.event_title || sessionToRemove.session_label || 'this host night'}"? This deletes the event and cannot be undone.`}
          confirmLabel="Remove night"
          danger
          submitting={busy}
          onConfirm={removeSession}
          onCancel={() => setSessionToRemove(null)}
        />
      )}

      {showFinalizeConfirm && (
        <ConfirmModal
          title={`Finalize Week ${week.week_number}?`}
          message="This pays out the weekly pot, awards season points and badges, and updates every player's handicap. It cannot be undone."
          confirmLabel="Finalize week"
          danger
          submitting={busy}
          onConfirm={finalizeWeek}
          onCancel={() => setShowFinalizeConfirm(false)}
        />
      )}
    </DSCard>
  );
}

const MACHINE_STATUS_LABELS = {
  needs_attention: 'needs attention',
  out_of_order: 'out of order',
  in_tournament: 'in tournament',
  removed: 'removed'
};

// How often each machine shows up across every week's lineup, plus which machines the
// randomizer never even considered, so an organizer can see both the machine it leaned on
// too hard and the ones quietly sitting out the season.
function MachineUsageSection({ weeks, machines }) {
  const holes = weeks.flatMap(week => week.lineup || []);
  // Machines the randomizer skips entirely. Anything not operational can still be picked
  // by hand for a hole, it just never comes up in a generated lineup.
  const excluded = machines.filter(machine => !isAutoPickable(machine));
  if (holes.length === 0 && excluded.length === 0) {
    return null;
  }

  const machineNames = new Map(machines.map(machine => [String(machine.id), machine.name]));
  const counts = new Map();
  for (const hole of holes) {
    const key = String(hole.machineId);
    counts.set(key, (counts.get(key) || 0) + 1);
  }

  const rows = Array.from(counts.entries())
    .map(([machineId, count]) => ({
      machineId,
      // A machine retired since the lineup was saved won't be in the picker list.
      name: machineNames.get(machineId) || `Machine #${machineId}`,
      count
    }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));

  const maxCount = rows.length > 0 ? rows[0].count : 0;
  // Eligible machines that still didn't make any week - with balanced picking this only
  // happens when there are more machines than the season has holes for.
  const unused = machines
    .filter(machine => isAutoPickable(machine) && !counts.has(String(machine.id)))
    .map(machine => machine.name)
    .sort((a, b) => a.localeCompare(b));

  // A machine with no target score can still be drawn (deliberately - the gap is surfaced
  // rather than hidden by shrinking the pool), but the hole is unscoreable until it's set.
  const needsTarget = machines
    .filter(machine => counts.has(String(machine.id)))
    .filter(machine => !(Number(machine.pingolf_target_score) > 0))
    .map(machine => machine.name)
    .sort((a, b) => a.localeCompare(b));

  const excludedSummary = excluded
    .slice()
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    .map((machine) => {
      const reason = autoPickBlocker(machine);
      return `${machine.name} (${MACHINE_STATUS_LABELS[reason] || reason})`;
    })
    .join(', ');

  return (
    <section className="pl-admin-section">
      <div className="pl-admin-week-section-title">Machine usage</div>
      {holes.length > 0 && (
        <p className="pl-admin-field-help">
          Across all {weeks.length} week{weeks.length === 1 ? '' : 's'}, {holes.length} hole{holes.length === 1 ? '' : 's'} total.
          Generated lineups spread the floor evenly, so counts should sit within one of each other.
        </p>
      )}

      {rows.length > 0 && (
        <ul className="pl-admin-usage-list">
          {rows.map(row => (
            <li key={row.machineId} className="pl-admin-usage-item">
              <span className="pl-admin-usage-name">{row.name}</span>
              <span className="pl-admin-usage-bar" aria-hidden="true">
                <span
                  className={`pl-admin-usage-bar-fill ${row.count === maxCount && maxCount > 1 ? 'is-top' : ''}`}
                  style={{ width: `${(row.count / maxCount) * 100}%` }}
                />
              </span>
              <span className="pl-admin-usage-count">
                {row.count}x
              </span>
            </li>
          ))}
        </ul>
      )}

      {unused.length > 0 && (
        <p className="pl-admin-field-help">
          <strong>Operational but not drawn yet ({unused.length}):</strong> {unused.join(', ')}. Once every
          machine has been used the same number of times, the extras sit out - there are only so many holes
          in a season.
        </p>
      )}

      {needsTarget.length > 0 && (
        <p className="pl-admin-field-help">
          <strong>Picked but missing a target score ({needsTarget.length}):</strong> {needsTarget.join(', ')}. Those
          holes stay blank and hold their week back from host-night generation until a target is set.
        </p>
      )}

      {excluded.length > 0 && (
        <p className="pl-admin-field-help">
          <strong>Excluded from randomizing ({excluded.length}):</strong> {excludedSummary}. Only operational
          machines get picked automatically - set one back to operational to bring it into the rotation, or
          add it to a week by hand.
        </p>
      )}

    </section>
  );
}

function PinGolfLeagueAdminSeason() {
  const { seasonId } = useParams();
  const navigate = useNavigate();
  // League organizers run everything else on this page; only an admin may tear a season down.
  const isAdmin = authService.getCurrentUser()?.role === 'admin';
  const [season, setSeason] = useState(null);
  const [weeks, setWeeks] = useState([]);
  const [hostSlots, setHostSlots] = useState([]);
  const [machines, setMachines] = useState([]);
  const [hosts, setHosts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showFinalizeSeasonConfirm, setShowFinalizeSeasonConfirm] = useState(false);
  const [showRefreshTargetsConfirm, setShowRefreshTargetsConfirm] = useState(false);

  const fetchSeasonDetail = useCallback(async () => {
    const [seasonRes, weeksRes, hostSlotsRes] = await Promise.all([
      apiService.getPingolfSeason(seasonId),
      apiService.getPingolfSeasonWeeks(seasonId),
      apiService.getPingolfHostSlots(seasonId)
    ]);
    setSeason(seasonRes.data);
    setWeeks(weeksRes.data);
    setHostSlots(hostSlotsRes.data);
  }, [seasonId]);

  useEffect(() => { fetchSeasonDetail(); }, [fetchSeasonDetail]);

  useEffect(() => {
    const fetchPickers = async () => {
      try {
        const [machinesRes, hostsRes] = await Promise.all([
          apiService.get('/machines'),
          apiService.get('/users?hostEligible=true')
        ]);
        setMachines((machinesRes.data || []).filter(machine => machine.status !== 'removed'));
        setHosts(hostsRes.data || []);
      } catch (error) {
        showToast('Failed to load machines/hosts for setup', 'error');
      }
    };
    fetchPickers();
  }, []);

  const generateHostNights = async () => {
    setBusy(true);
    try {
      const result = await apiService.generatePingolfHostNights(seasonId);
      const { created, skipped } = result.data;
      if (created.length > 0) {
        showToast(`${created.length} host night${created.length === 1 ? '' : 's'} created - scroll down to the week${created.length === 1 ? '' : 's'} below`, 'success');
      }
      if (skipped.length > 0) {
        showToast(`${skipped.length} skipped: ${summarizeSkipped(skipped)}`, created.length > 0 ? 'warning' : 'error');
      }
      await fetchSeasonDetail();
    } catch (error) {
      showToast(error.response?.data?.error || 'Failed to generate host nights', 'error');
    } finally {
      setBusy(false);
    }
  };

  // The one-button setup path. Weeks (with their randomized lineups) have to exist before
  // host nights can be placed against them, so this runs the server's two steps back to
  // back rather than making the organizer find both in the Actions menu.
  const generateSchedule = async () => {
    setBusy(true);
    try {
      await apiService.generatePingolfSeasonWeeks(seasonId);
      const result = await apiService.generatePingolfHostNights(seasonId);
      const { created, skipped } = result.data;
      showToast(
        `${season.week_count} weeks and ${created.length} host night${created.length === 1 ? '' : 's'} created`,
        'success'
      );
      if (skipped.length > 0) {
        showToast(`${skipped.length} skipped: ${summarizeSkipped(skipped)}`, created.length > 0 ? 'warning' : 'error');
      }
      await fetchSeasonDetail();
    } catch (error) {
      showToast(error.response?.data?.error || 'Failed to generate the schedule', 'error');
    } finally {
      setBusy(false);
    }
  };

  // Weeks and nights copy each machine's target when they are created. A machine target edit
  // now follows into every upcoming copy on its own; this is the catch-up for a season whose
  // weeks were made before that, or after a failed push.
  const refreshTargets = async () => {
    setBusy(true);
    try {
      const result = await apiService.refreshPingolfSeasonTargets(seasonId);
      showToast(describeTargetRefresh(result.data, { announceNoChange: true }), 'success', 6000);
      setShowRefreshTargetsConfirm(false);
      await fetchSeasonDetail();
    } catch (error) {
      showToast(error.response?.data?.error || 'Failed to refresh targets from machines', 'error');
    } finally {
      setBusy(false);
    }
  };

  const finalizeSeason = async () => {
    setBusy(true);
    try {
      const result = await apiService.finalizePingolfSeason(seasonId);
      showToast(`Season finalized - $${result.data.season_pool_amount} pool paid out`, 'success');
      setShowFinalizeSeasonConfirm(false);
      await fetchSeasonDetail();
    } catch (error) {
      showToast(error.response?.data?.error || 'Failed to finalize season', 'error');
    } finally {
      setBusy(false);
    }
  };

  const deleteSeason = async () => {
    setBusy(true);
    try {
      const { data } = await apiService.deletePingolfSeason(seasonId, season.name);
      // Report what was actually reversed rather than a bare "deleted". The whole point of
      // the teardown is the cleanup on other people's accounts, and that is invisible from
      // this screen - a plain success message would leave the admin to hope it happened.
      const reversed = data?.reversed;
      showToast(
        reversed
          ? `Season deleted - reversed ${reversed.handicaps_reverted} handicaps, ${reversed.credit_transactions_removed} credit transactions, ${reversed.badges_revoked} badges`
          : 'Season deleted',
        'success'
      );
      navigate('/pingolf-season/admin');
    } catch (error) {
      showToast(error.response?.data?.error || 'Failed to delete season', 'error');
    } finally {
      setBusy(false);
      setShowDeleteConfirm(false);
    }
  };

  if (!season) {
    return (
      <div className="pl-admin-page bn-page-padding">
        <PinGolfBottomNav />
      </div>
    );
  }

  const seasonActions = [
    season.status !== 'completed' && { label: 'Refresh targets from machines', onClick: () => setShowRefreshTargetsConfirm(true), disabled: busy },
    season.status !== 'completed' && { label: 'Finalize season', onClick: () => setShowFinalizeSeasonConfirm(true), disabled: busy, tone: 'danger' },
    { label: 'Public season page', onClick: () => window.open(`/pingolf-season/${season.id}`, '_blank', 'noopener') },
    { label: 'TV broadcast', onClick: () => window.open(`/pingolf-season/${season.id}/tv`, '_blank', 'noopener') },
    // Offered on a finalized season too, and admin-only: deleting now reverses the season's
    // handicap contributions, payouts and badges on every participant's account, which is a
    // bigger power than running the league. The server enforces both.
    isAdmin && { label: 'Delete season', onClick: () => setShowDeleteConfirm(true), disabled: busy, tone: 'danger' }
  ];

  return (
    <div className="pl-admin-page bn-page-padding">
      <div className="pl-admin-hero" style={{ backgroundImage: `url(${pingolfHero})` }}>
        <div className="pl-admin-hero-overlay" />
        <div className="pl-admin-hero-content">
          <DSBackLink to="/pingolf-season/admin">All seasons</DSBackLink>
          <div className="pl-admin-hero-title-row">
            <h1>{season.name}</h1>
          </div>
          <div className="pl-admin-hero-subtitle-row">
            <span className="pl-admin-hero-subtitle-row-info">
              <p className="pl-admin-hero-subtitle">Season {season.season_number}</p>
              <DSBadge variant={SEASON_STATUS_VARIANT[season.status] || 'info'} appearance="outline">{season.status}</DSBadge>
            </span>
            <ActionsMenu actions={seasonActions} />
          </div>
        </div>
      </div>

      <PinGolfSeasonSettings
        season={season}
        cardsSold={Number(season.cards_sold) || 0}
        onChanged={fetchSeasonDetail}
      />

      <HostSlotsSection
        season={season}
        hostSlots={hostSlots}
        hosts={hosts}
        weeksGenerated={weeks.length > 0}
        weeksMissingSessions={weeks.filter(week => (week.sessions || []).length === 0).length}
        onGenerateSchedule={generateSchedule}
        onGenerateHostNights={generateHostNights}
        generating={busy}
        onChanged={fetchSeasonDetail}
      />

      {weeks.map(week => (
        <WeekCard key={week.id} week={week} weeks={weeks} machines={machines} hosts={hosts} onChanged={fetchSeasonDetail} />
      ))}

      <MachineUsageSection weeks={weeks} machines={machines} />

      <PinGolfBottomNav />

      {showRefreshTargetsConfirm && (
        <ConfirmModal
          title="Refresh targets from machines?"
          message="Copies each machine's current PinGolf target onto every upcoming hole and week lineup in this season. Nights that have sold a card or already happened keep their targets, and so does every night of a week where one night has. Holes given their own target are left alone."
          confirmLabel="Refresh targets"
          submitting={busy}
          onConfirm={refreshTargets}
          onCancel={() => setShowRefreshTargetsConfirm(false)}
        />
      )}

      {showFinalizeSeasonConfirm && (
        <ConfirmModal
          title="Finalize season?"
          message={`Finalize "${season.name}"? This pays out the season prize pool, awards the season badges, and closes the season for good. It cannot be undone.`}
          confirmLabel="Finalize season"
          danger
          submitting={busy}
          onConfirm={finalizeSeason}
          onCancel={() => setShowFinalizeSeasonConfirm(false)}
        />
      )}

      {showDeleteConfirm && (
        <ConfirmModal
          title="Delete season?"
          message={`Delete "${season.name}"? This removes its weeks, host nights and host slots, and reverses everything the season did to member accounts: handicap contributions, card purchases and payouts, badges earned, and the notifications about them. Cannot be undone.`}
          confirmLabel="Delete season"
          confirmPhrase={season.name}
          danger
          submitting={busy}
          onConfirm={deleteSeason}
          onCancel={() => setShowDeleteConfirm(false)}
        />
      )}
    </div>
  );
}

export default PinGolfLeagueAdminSeason;
